<template>
	<div id="root" ref="root" :style="`height: ${height}; width: ${width}`">
		<KnowBe4 v-if="!hideLogos" />
		<PhishER v-if="!hideLogos" />
		<Clock v-if="!hideLogos" />
		<div v-if="isMounted" id="map" :style="`height: ${offsetHeight}px; width: ${offsetWidth}px`">
			<MapPaths :width="offsetWidth" :height="offsetHeight" class='fullsize' :mapping="mapping" />
			<svg class="fullsize">
				<HexbinGrid :mapping="mapping" :traces="traces" :recentTraces="recentTraces" />
			</svg>
			<TracePath v-for="trace in recentTraces" :width="offsetWidth" :height="offsetHeight" :key='trace.id' class="fullsize" :mapping="mapping" :trace="trace" />
		</div>
		<Log :traces="traces"/>
	</div>
</template>

<script>
	import * as d3 from "d3";
	import {hexbin} from "d3-hexbin";
	d3.hexbin = hexbin;

	import {differenceInSeconds} from "date-fns";

	import KnowBe4 from './components/ui/logos/KnowBe4.vue';
	import PhishER from './components/ui/logos/PhishER.vue';
	import Clock from './components/ui/Clock.vue';
	import MapPaths from './components/map/MapPaths.vue';
	import HexbinGrid from './components/paths/HexbinGrid.vue';
	import TracePath from './components/canvas/TracePath.vue';
	import Log from './components/ui/Log.vue';

	export default {
		name: 'PABMap',

		components: {
			KnowBe4,
			PhishER,
			Clock,
			MapPaths,
			HexbinGrid,
			TracePath,
			Log
		},

		props: {
			height: {
				default: "",
			},
			width: {
				default: "",
			},
			snapToGrid: {
				default: true,
			},
			showIntermediateNodes: {
				default: false,
			},
			hideLogos: {
				default: false
			}
		},

		data() {
			return {
				isMounted: false,
				offsetHeight: 0,
				offsetWidth: 0,
				intervalRef: null,
				intervalCount: 0,
				mapping: {},
				traces: [],
				recentTraces: [],
				traceQueue: [],
				hidden: false
			}
		},

		created() {
			const { mqttClient } = this.$store.state;
			mqttClient.on('message', this.onMessage);
		},

		mounted() {
			this.onResize();
			this.isMounted = true;
			window.addEventListener("resize", this.onResize);
			document.addEventListener("visibilitychange", this.onVisibilityChange);
		},

		watch: {
			traces: function(traces) {
				this.recentTraces = _.filter(traces, (trace) => {
					// TODO: do this differently.
					let diff = differenceInSeconds(new Date(), trace.added);
					// console.log("DIFF: ", diff);
					return diff <= 2;
				});
			}
		},

		methods: {
			onResize() {
				this.offsetWidth = this.$refs.root.offsetWidth;
				this.offsetHeight = (837.03125 / 1920) * this.offsetWidth;
				this.mapping = {};
				this.mapping.showIntermediateNodes = this.showIntermediateNodes;

				// this.mapping.projection = d3.geoEquirectangular()
				// 	.center([0, 15]) // set centre to further North as we are cropping more off bottom of map
				// 	.scale([this.offsetWidth / (2 * Math.PI)]) // scale to fit group width
				// 	.translate([this.offsetWidth / 2, this.offsetHeight / 2]) // ensure centred in group

				this.mapping.projection = d3.geoNaturalEarth1()
					.center([0, 15])
					.scale(this.offsetWidth / 5.40432)
					.translate([this.offsetWidth / 2, (this.offsetHeight / 2) - (this.offsetHeight * 0.027044820153537426)]);

				this.mapping.hexbin = d3.hexbin()
					.size([this.offsetWidth, this.offsetHeight])
					.radius(this.offsetWidth / 192);

				this.mapping.lngLatToRawXY = (lng, lat) => this.mapping.projection([lng, lat]);

				this.mapping.lngLatToXY = (lng, lat) => {
					const pos = this.mapping.lngLatToRawXY(lng, lat);
					if (!this.snapToGrid) {
						return pos;
					}
					const hex = this.mapping.hexbin([pos])[0];
					return [hex.x, hex.y];
				};

				this.traces = _.chain(this.traces).map((trace) => {
					return this.updateNodePositions(trace);
				}).value();
			},

			onVisibilityChange() {
				const {visibilityState} = document;
				if (visibilityState === 'hidden') {
					this.hidden = true;
				} else {
					this.hidden = false;
				}
			},

			load: _.throttle(function(trace) {
			  if (this.traces.length > 20) { this.traces.shift(); }
				this.traces.push(this.updateNodePositions(trace));
			}, 100),

			updateNodePositions(trace) {
				trace.nodes = _.chain(trace.nodes).map((node) => {
					node.pos = this.mapping.lngLatToXY(node.lng, node.lat);
					node.x = node.pos[0];
					node.y = node.pos[1];
					return node;
				}).value();

				return trace;
			},

			onMessage(trace) {
				if (this.hidden) return;
				if (trace.nodes.length < 2) return;

				const first = _.first(trace.nodes);
				const last = _.last(trace.nodes);
				const dist = Math.sqrt(Math.pow(first.lng - last.lng, 2) + Math.pow(first.lat - last.lat, 2));

				if (dist === 0) {
					return;
				}

				trace.id = this.intervalCount++;
				trace.added = new Date();
				trace.nodes = trace.nodes.map((node, i) => {
					node.id = `${trace.id}:${i}`;
					return node;
				});

				this.load(trace);
			}
		}
	}
</script>

<style scoped lang="scss">
	#root {
		display: flex;
		justify-content: center;
		align-items: center;
		width: 100%;
		height: 100%;
		background-color: #222234;
		overflow: hidden;
	}
	#map {
		position: relative;
	}
	.fullsize {
		position: absolute;
		top: 0;
		left: 0;
		width: 100%;
		height: 100%;
	}
</style>
